import { CreateOrderPayload } from '../../../services/order-creation.service';

export interface OrderRequestCheckoutValidationInput {
  hasWashPackage: boolean;
  hasCommittedAddons: boolean;
  licensePlateRequired: boolean;
  hasLicensePlate: boolean;
  licensePlateInvalid?: boolean;
  isMonthlyPackage: boolean;
  /**
   * CWS-1880: a prepaid wash book is being bought. The purchase SMS and email (QR, code,
   * instructions) are the only way the customer can redeem later, so a sale with no phone
   * leaves them with nothing to present at the lane. Email stays optional.
   */
  isPrepaidWashBookPurchase?: boolean;
  hasCustomerPhone: boolean;
  phoneInvalid: boolean;
  emailInvalid: boolean;
  firstNameInvalid?: boolean;
  lastNameInvalid?: boolean;
}

export interface CreateOrderValidationOptions {
  /** When false, `package_id` may be omitted if add-ons are present. Default true. */
  requirePackage?: boolean;
  /** When false, `vehicle_id` may be omitted (addon-only / no plate required). Default true. */
  requireVehicle?: boolean;
}

export function hasOrderRequestCheckoutLineItems(input: {
  hasWashPackage: boolean;
  hasCommittedAddons: boolean;
  hasPrepaidSelection?: boolean;
  hasPrepaidRedemptionSelection?: boolean;
}): boolean {
  return (
    input.hasWashPackage ||
    input.hasCommittedAddons ||
    !!input.hasPrepaidSelection ||
    !!input.hasPrepaidRedemptionSelection
  );
}

export function isOrderRequestLicensePlateRequired(input: {
  washPackageNeedsPlate?: boolean;
  selectedAddonNeedsPlate?: boolean;
}): boolean {
  return !!input.washPackageNeedsPlate || !!input.selectedAddonNeedsPlate;
}

/** POST /order: include `vehicle_id` when an id is available (even if plate is optional). */
export function buildCreateOrderVehicleIdField(
  vehicleId?: string | null
): Pick<CreateOrderPayload, 'vehicle_id'> {
  const id = String(vehicleId ?? '').trim();
  if (!id) {
    return {};
  }
  return { vehicle_id: id };
}

/**
 * POST /order and PUT /order/:id: omit `package_id` for prepaid wash book orders.
 *
 * Both validators declare it `Joi.forbidden()` for these wash types, which rejects
 * the key being present at all — an empty string is a 400, not a no-op. Callers must
 * leave it out rather than blank it.
 */
export function buildCreateOrderPackageIdField(
  washType: CreateOrderPayload['wash_type'],
  packageId?: string | null
): Pick<CreateOrderPayload, 'package_id'> {
  if (washType === 'prepaid_wash_book' || washType === 'prepaid_wash_redemption') {
    return {};
  }
  const id = String(packageId ?? '').trim();
  return { package_id: id };
}

/**
 * CWS-1181: drop the prepaid wash book id from the checkout snapshot once the order is
 * no longer a prepaid purchase.
 *
 * `isCheckoutPrepaidPurchase()` reads the live UI selection first but then falls back to
 * `notification.data.order.prepaid_wash_book_id` / `notification.data.prepaid_wash_book_id`,
 * which POST /order wrote for the prepaid order. Selecting a package clears the live
 * selection but left those keys behind, so the fallback still answered "prepaid" and
 * `getNormalizedWashType()` returned `prepaid_wash_book` no matter what the attendant
 * picked — the membership never reached the API and came back as prepaid.
 *
 * Mutates in place, matching how the component already syncs `wash_type` onto the
 * notification. Returns the keys it cleared so callers can log or assert on them.
 */
export function clearPrepaidWashBookContext(
  data: Record<string, any> | null | undefined
): string[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  const cleared: string[] = [];
  const drop = (target: Record<string, any>, key: string, label: string): void => {
    if (target[key] == null || String(target[key]).trim() === '') {
      return;
    }
    delete target[key];
    cleared.push(label);
  };

  drop(data, 'prepaid_wash_book_id', 'prepaid_wash_book_id');
  drop(data, 'prepaidWashBookId', 'prepaidWashBookId');

  const order = data['order'];
  if (order && typeof order === 'object' && !Array.isArray(order)) {
    drop(order, 'prepaid_wash_book_id', 'order.prepaid_wash_book_id');
    drop(order, 'prepaidWashBookId', 'order.prepaidWashBookId');
  }

  return cleared;
}

/** Shared order-request field / footer copy (sentence case, trailing period). */
export const ORDER_REQUEST_PLATE_REQUIRED_MESSAGE = 'Plate number is required.';
export const ORDER_REQUEST_PLATE_INVALID_MESSAGE =
  'License plate can only contain letters and numbers.';
export const ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE =
  'Phone number is required for monthly packages.';
/**
 * CWS-1881: the walked-in plate is registered to a third customer, so it is neither the
 * redeemer's to reuse nor ours to reassign. Matches the wording the family flow uses for
 * the same backend refusal.
 */
export const PREPAID_REDEMPTION_PLATE_TAKEN_MESSAGE =
  'This plate belongs to another customer.';
export const ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE =
  'Phone number is required for prepaid packages.';
export const ORDER_REQUEST_LANE_REQUIRED_MESSAGE = 'Lane is required.';
export const ORDER_REQUEST_LANES_LOADING_MESSAGE =
  'Lanes are still loading for this location. Please wait a moment.';
export const ORDER_REQUEST_LANES_LOAD_FAILED_MESSAGE =
  'Could not load lanes for this location. Please try again.';
export const ORDER_REQUEST_LINE_ITEMS_REQUIRED_MESSAGE =
  'Select a package, prepaid wash book, or at least one add-on.';
export const ORDER_REQUEST_VALID_PHONE_MESSAGE = 'Please enter a valid phone number.';
export const ORDER_REQUEST_VALID_EMAIL_MESSAGE = 'Please enter a valid email address.';

export function getOrderRequestCheckoutValidationError(
  input: OrderRequestCheckoutValidationInput
): string | null {
  if (input.licensePlateRequired && !input.hasLicensePlate) {
    return ORDER_REQUEST_PLATE_REQUIRED_MESSAGE;
  }
  if (input.licensePlateInvalid) {
    return ORDER_REQUEST_PLATE_INVALID_MESSAGE;
  }
  if (input.isMonthlyPackage && !input.hasCustomerPhone) {
    return ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE;
  }
  if (input.isPrepaidWashBookPurchase && !input.hasCustomerPhone) {
    return ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE;
  }
  if (input.phoneInvalid) {
    return ORDER_REQUEST_VALID_PHONE_MESSAGE;
  }
  if (input.emailInvalid) {
    return ORDER_REQUEST_VALID_EMAIL_MESSAGE;
  }
  if (input.firstNameInvalid) {
    return 'Please enter a valid first name.';
  }
  if (input.lastNameInvalid) {
    return 'Please enter a valid last name.';
  }
  return null;
}

export function isOrderRequestCheckoutValid(input: OrderRequestCheckoutValidationInput): boolean {
  return getOrderRequestCheckoutValidationError(input) == null;
}

export function validateCreateOrderPayload(
  payload: CreateOrderPayload,
  options: CreateOrderValidationOptions = {}
): { isValid: boolean; errors: string[] } {
  const requirePackage = options.requirePackage !== false;
  const requireVehicle = options.requireVehicle !== false;
  const hasAddons = (payload.add_on_variants?.length ?? 0) > 0;
  const hasPrepaidWashBook =
    payload.wash_type === 'prepaid_wash_book' &&
    !!(payload.prepaid_wash_book_id && String(payload.prepaid_wash_book_id).trim());
  const hasPackage = !!(payload.package_id && String(payload.package_id).trim());
  const errors: string[] = [];

  if (!payload.customer_id) {
    errors.push('Customer ID is required');
  }
  if (requirePackage && !hasPackage && !hasAddons && !hasPrepaidWashBook) {
    errors.push(ORDER_REQUEST_LINE_ITEMS_REQUIRED_MESSAGE);
  }
  if (!payload.location_id) {
    errors.push('Location ID is required');
  }
  if (requireVehicle && !payload.vehicle_id) {
    errors.push('Vehicle ID is required');
  }
  if (!payload.wash_type) {
    errors.push('Wash type is required');
  }
  if (!payload.appointment_time) {
    errors.push('Appointment time is required');
  }
  if (!payload.source) {
    errors.push('Source is required');
  }

  return { isValid: errors.length === 0, errors };
}
